import type { FC } from 'react';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { SortableGridRenderItem } from 'react-native-sortables';
import Sortable from 'react-native-sortables';
import type { DragEndParams } from 'react-native-sortables/dist/typescript/types';

import { THUMB_SIZE } from '@/features/media/components/MediaImageSized';
import type { DetailImageField } from '@/features/profile-setting/components/organization-edit/types';

const GRID_GAP = 8;

// Drag-and-drop tuning for the active (dragged) tile.
const ACTIVE_ITEM_SCALE = 1.0125;
const ACTIVE_ITEM_SHADOW_OPACITY = 0.25;
const DRAG_ACTIVATION_DELAY_MS = 100;
const ACTIVATION_ANIMATION_DURATION_MS = 100;

type Props = {
  fields: DetailImageField[];
  /** Disables drag-and-drop reordering (images render as a static grid). */
  disabled?: boolean;
  /** Stable render prop (e.g. wrapped in useCallback) matching the grid's
   * RenderItem signature, so it can be passed straight to Sortable.Grid. */
  renderTile: SortableGridRenderItem<DetailImageField>;
  onDropped: (params: DragEndParams) => void;
  onDragStart: () => void;
};

export const DetailImageGrid: FC<Props> = ({
  fields,
  disabled,
  renderTile,
  onDropped,
  onDragStart
}) => {
  // Undefined until the first layout pass has measured the available width.
  const [availableWidth, setAvailableWidth] = useState<number>();

  if (fields.length === 0) {
    return <></>;
  }

  if (disabled) {
    return (
      <View style={styles.grid}>
        {fields.map((field, index) => (
          <View key={field.fieldId}>{renderTile({ item: field, index })}</View>
        ))}
      </View>
    );
  }

  // First render: full-width, invisible measuring pass.
  if (availableWidth === undefined) {
    return (
      <View
        onLayout={(e) => {
          setAvailableWidth(e.nativeEvent.layout.width);
        }}
      />
    );
  }

  // Sortable.Grid stretches every cell to fill its width, so any slack would
  // show up as inflated gaps. Shrink-wrap the grid to the exact tile span.
  const columns = Math.max(
    1,
    Math.floor((availableWidth + GRID_GAP) / (THUMB_SIZE + GRID_GAP))
  );
  const gridWidth = columns * (THUMB_SIZE + GRID_GAP) - GRID_GAP;

  return (
    <View style={{ width: gridWidth }}>
      <Sortable.Grid
        data={fields}
        keyExtractor={(item) => item.key}
        renderItem={renderTile}
        columns={columns}
        rowGap={GRID_GAP}
        columnGap={GRID_GAP}
        onActiveItemDropped={onDropped}
        onDragStart={onDragStart}
        activeItemScale={ACTIVE_ITEM_SCALE}
        activeItemShadowOpacity={ACTIVE_ITEM_SHADOW_OPACITY}
        dragActivationDelay={DRAG_ACTIVATION_DELAY_MS}
        activationAnimationDuration={ACTIVATION_ANIMATION_DURATION_MS}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: GRID_GAP }
});
